// @flow

import _ from 'lodash';
import React, { type ComponentType } from 'react';
import { useQuery } from '@apollo/client';
import type { DocumentNode } from 'graphql';

import { EXCHANGE_TYPES } from 'client/constants/health';
import AddressInput from 'client/models/health/AddressInput';
import { getBaseApplicantInputs, hasMissingMemberInfo } from 'client/utils/health/member';
import { transformHealthPlans } from 'client/utils/health/plan';
import { isStrideConsumer } from 'client/utils/configUtils';

import type { AddressType } from 'web-core/types/address';
import type { MemberType } from 'web-core/types/member';
import type { METAL_TYPES, SubsidyType } from 'client/constants/health';
import type { RecommendationResponseType } from 'types/health/plan.flow';
import type { SessionMemberDrugType } from 'client/models/health/DrugInfoModel';

import fullQuery from 'client/queries/health/getPlanRecommendation';

type Props = {
  address: AddressType,
  isGoodDealPlanEnabled: boolean,
  memberDrugs: Array<SessionMemberDrugType>,
  members: Array<MemberType>,
  metalTypes: Array<METAL_TYPES>,
  onCompleted: Function,
  planYear: number,
  providers: Array<number>,
  query: DocumentNode,
  subsidy: SubsidyType,
  acceptedCarriers: Array<String>,
  siteConfigs: Object,
  isIsaAgent: Boolean,
  agentId: String,
  partnerUser: Object,
};

const withPlanRecommendationHook = (Component: ComponentType<any>) => {
  const WrappedComponent = (props: Props) => {
    const {
      address,
      isGoodDealPlanEnabled,
      memberDrugs,
      members,
      metalTypes = [],
      onCompleted = _.noop,
      planYear,
      providers,
      query = fullQuery,
      subsidy,
      acceptedCarriers,
      siteConfigs,
      isIsaAgent,
      agentId,
      partnerUser,
    } = props;

    const subsidyAmount = _.get(subsidy, 'amount', null);
    const isConsumer = isStrideConsumer({ siteConfigs, isIsaAgent, agentId, partnerUser });
    const { data }: { data: RecommendationResponseType } = useQuery(query, {
      skip: _.isEmpty(address) || hasMissingMemberInfo(members),
      variables: {
        ...new AddressInput(address || {}),
        applicants: getBaseApplicantInputs(members, null, providers, memberDrugs),
        subsidy: subsidyAmount,
        onExchange: subsidyAmount > 0 ? EXCHANGE_TYPES.TRUE : EXCHANGE_TYPES.CROSS,
        csr: _.get(subsidy, 'csr', null),
        planYear,
        metalTypes,
        filterOptions: {
          issuerIds: (acceptedCarriers || []).map(carrierId => parseInt(carrierId, 10)),
        },
      },
      onCompleted: () => {
        if (onCompleted && _.isFunction(onCompleted)) {
          onCompleted(
            transformHealthPlans(_.get(data, 'getHealthRecommendation.plans'), isConsumer),
          );
        }
      },
      context: {
        headers: {
          'x-feature-flags': isGoodDealPlanEnabled ? 'GOOD_DEAL' : '',
        },
        clientName: 'marketplace',
      },
    });
    let transformedPlans = null;

    transformedPlans = transformHealthPlans(
      _.get(data, 'getHealthRecommendation.plans'),
      isConsumer,
    );

    return <Component {...props} plans={transformedPlans} />;
  };

  return WrappedComponent;
};

export default withPlanRecommendationHook;
